import { getImage } from "api";
import { useEffect, useState, useRef } from "react";
import { getCachedImage, setCachedImage } from "utils";
import { useMainContext } from "contexts";
import styled from "styled-components";
import useAdBlockStore from "store/AdBlockStore";

export const PageImagePreview = ({
    filename = '',
    style = {},
    pageWidth = 0,
    pageHeight = 0,
    showOverlay = true,
}) => {
    const adblockCoords = useAdBlockStore((state) => state.adblockCoords);
    const sectionCoords = useAdBlockStore((state) => state.sectionCoords);

    const ref = useRef(null);
    const newWindowRef = useRef(null);
    
    // create states for image and loading
    const [adBlockImg, setAdBlockImg] = useState(null);
    const [error, setError] = useState(null);
    const [clientWidth, setClientWidth] = useState(1);
    const [clientHeight, setClientHeight] = useState(1);
    const [adblockRect, setAdblockRect] = useState({
        x: 0,
        y: 0,
        w: 0,
        h: 0,
    });

    const fetchImage = async () => {
        // console.log('Fetching image:', filename);
        try {
            // Check if the image is cached
            const cachedImage = await getCachedImage(filename);
            // console.log('Cached image:', cachedImage);
            if (cachedImage) {
                // Convert base64 to data URI for display
                setAdBlockImg(`data:image/jpeg;base64,${cachedImage}`);
                return;
            }
            console.log('Image not cached, fetching from API:', filename);
            // Fetch the image from the API
            const img = await getImage(filename);
            
            // console.log('IMG', img)
            if (img.success) {
                // Cache the fetched image
                await setCachedImage(filename, img.payload);
                // Convert base64 to data URI for display
                setAdBlockImg(`data:image/jpeg;base64,${img.payload}`);
            }
        } catch (error) {
            setError(error);
        }
    };

    useEffect(() => {
        if (filename) {
            // Clear the previous image immediately to prevent flash of old content
            setAdBlockImg(null);
            fetchImage();
        }
    }, [filename]);

    // Handle client dimensions
    useEffect(() => {
        if (ref.current) {
            setClientWidth(ref.current.clientWidth);
            setClientHeight(ref.current.clientHeight);
        }
    }, [ref]);

    // Calculate adblock rectangle position
    useEffect(() => {
        if (
            adBlockImg &&
            pageWidth > 1 &&
            pageHeight > 1 &&
            showOverlay &&
            (adblockCoords.coords_x1 > 0 ||
                adblockCoords.coords_x2 > 0 ||
                adblockCoords.coords_y1 > 0 ||
                adblockCoords.coords_y2 > 0)
        ) {
            let offsetX = 0,
                offsetY = 0,
                adblockWidth,
                adblockHeight;
            const clientRatio = clientWidth / clientHeight;
            const adblockRatio = pageWidth / pageHeight;
            if (adblockRatio < clientRatio) {
                offsetY = 0;
                offsetX = (clientWidth - clientHeight * adblockRatio) / 2;
                adblockWidth = clientHeight * adblockRatio;
                adblockHeight = clientHeight;
            } else {
                offsetY = (clientHeight - clientWidth / adblockRatio) / 2;
                offsetX = 0;
                adblockWidth = clientWidth;
                adblockHeight = clientWidth / adblockRatio;
            }
            setAdblockRect({
                x: offsetX + adblockWidth * (adblockCoords.coords_x1 / pageWidth),
                y: offsetY + adblockHeight * (adblockCoords.coords_y1 / pageHeight),
                w:
                    adblockWidth *
                    ((adblockCoords.coords_x2 - adblockCoords.coords_x1) / pageWidth),
                h:
                    adblockHeight *
                    ((adblockCoords.coords_y2 - adblockCoords.coords_y1) / pageHeight),
            });
        } else {
            // Unset red rectangle
            setAdblockRect({
                x: 0,
                y: 0,
                w: 0,
                h: 0,
            });
        }
        
        // Auto update image and coords for child window
        if (newWindowRef.current && !newWindowRef.current.closed) {
            updateChildWindow();
        }
    }, [adBlockImg, pageWidth, pageHeight, adblockCoords, sectionCoords, showOverlay, clientWidth, clientHeight]);

    const openAdblockWindow = () => {
        // Check if window exists and isn't closed somehow?
        if (newWindowRef.current && !newWindowRef.current.closed) {
            // Send msg to existing window
            updateChildWindow();
        } else {
            // Open new window
            newWindowRef.current = window.open(
                "/adblock-view-location",
                "AdBlockLocationView",
                `width=${screen.availWidth},height=${screen.availHeight}`
            );

            // Send data when the window is first opened
            newWindowRef.current.onload = () => {
                updateChildWindow();
            };
        }
    };

    const updateChildWindow = () => {
        // Send msg to existing window
        if (newWindowRef.current && !newWindowRef.current.closed) {
            newWindowRef.current.postMessage(
                {
                    type: "UPDATE_DATA",
                    payload: { backImg: adBlockImg, pageWidth, pageHeight, adblockCoords, sectionCoords },
                },
                window.origin // current origin
            );
        }
    };

    const imageStyle = {
        maxWidth: '100%',
        maxHeight: '100%',
        width: 'auto',
        height: 'auto',
        objectFit: 'contain',
        display: 'block',
        ...style,
    };

    return (
        <Container 
            ref={ref}
            onClick={showOverlay ? openAdblockWindow : undefined}
        >
            {error && <div className="error-message">Error loading image: {error.message}</div>}
            {adBlockImg ? (
                <>
                    <img 
                        src={adBlockImg} 
                        alt="Ad Block" 
                        style={imageStyle}
                    />
                    {showOverlay && adblockRect.w > 0 && adblockRect.h > 0 && (
                        <div
                            style={{
                                position: 'absolute',
                                left: `${adblockRect.x}px`,
                                top: `${adblockRect.y}px`,
                                width: `${adblockRect.w}px`,
                                height: `${adblockRect.h}px`,
                                border: '2px solid red',
                                pointerEvents: 'none', // Allow clicks to pass through to parent
                            }}
                        />
                    )}
                </>
            ) : (
                <div className="loading-message">Loading image...</div>
            )}
        </Container>
    );
}

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
    cursor: pointer;
    transition: opacity 300ms ease;
    &:hover {
        opacity: ${(props) => props.showOverlay ? 0.8 : 1};
    }
`;
